import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { GRANT_OPTIONS, ROLE_OPTIONS } from "../../constants/adminConstants";

const ROLE_COLOUR = { ADMIN: "primary", READONLY: "warning", AGENT: "secondary" };

export default function UsersTable({
  users,
  loading,
  onResetClick,
  onToggleEnabled,
  onChangeRole,
  onToggleGrant,
}) {
  // The backend refuses self-demotion and self-disable (403); greying the
  // controls on your own row just says so up front.
  const me = (sessionStorage.getItem("userEmail") || "").toLowerCase();

  if (loading) {
    return (
      <Box py={4} display="flex" justifyContent="center">
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Level</TableCell>
            <TableCell>Grants</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => {
            const isMe = Boolean(me) && (user.email || "").toLowerCase() === me;
            const isAdmin = user.role === "ADMIN";
            const grants = user.grants || [];
            return (
              <TableRow key={user.id} hover>
                <TableCell>{user.id}</TableCell>
                <TableCell>{`${user.firstname || ""} ${user.lastname || ""}`.trim()}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Tooltip title={isMe ? "You cannot change your own level" : ""}>
                    <span>
                      <Select
                        size="small"
                        value={user.role || "USER"}
                        disabled={isMe}
                        onChange={(e) => onChangeRole?.(user, e.target.value)}
                        renderValue={(v) => (
                          <Chip size="small" label={v} color={ROLE_COLOUR[v] || "default"} />
                        )}
                        sx={{ minWidth: 118, "& .MuiSelect-select": { py: 0.5, pl: 1 } }}
                      >
                        {ROLE_OPTIONS.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={isAdmin ? "ADMIN already has everything" : ""}>
                    <Box display="grid" gridTemplateColumns="auto auto auto" columnGap={1}>
                      {GRANT_OPTIONS.map((g) => (
                        <Tooltip key={g.key} title={g.hint}>
                          <FormControlLabel
                            sx={{
                              mr: 0,
                              "& .MuiTypography-root": { fontSize: 13 },
                              "& .MuiCheckbox-root": { py: 0.25 },
                            }}
                            label={g.label}
                            control={
                              <Checkbox
                                size="small"
                                checked={isAdmin || grants.includes(g.key)}
                                disabled={isAdmin}
                                onChange={() => onToggleGrant?.(user, g.key)}
                              />
                            }
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={user.enabled ? "Enabled" : "Disabled"}
                    color={user.enabled ? "success" : "default"}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack spacing={0.5} alignItems="flex-end">
                    <Button
                      size="small"
                      onClick={() => onResetClick(user)}
                      sx={{ height: 28, whiteSpace: "nowrap" }}
                    >
                      Reset password
                    </Button>
                    <Button
                      size="small"
                      color={user.enabled ? "warning" : "success"}
                      disabled={isMe}
                      onClick={() => onToggleEnabled(user)}
                      sx={{ height: 28 }}
                    >
                      {user.enabled ? "Disable" : "Enable"}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}

          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                No users found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
